import { useState, useRef, createRef } from 'react';
import DataGrid from '@/components/elements/lists/DataGrid';
import Icon from '@/components/icons/Icon';
import { CheckoutDetailsFee } from '@/types/checkout';

import { _t } from '@/locale';

import Tooltip from '@/components/modules/Tooltip';

type Props = {
  baseTranslationKey:
    | 'components.modules.checkout.CheckoutDetails'
    | 'components.modules.appointment.AppointmentDetailsSummary';
  fees?: CheckoutDetailsFee[];
};

const PaymentDetailsFeesList = ({ baseTranslationKey, fees }: Props) => {
  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null);
  const toolTipTargetRefs = useRef<React.RefObject<HTMLLIElement>[]>([]);
  const toolTipTriggerRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);

  (fees ?? []).forEach((_, i) => {
    if (!toolTipTriggerRefs.current[i]) {
      toolTipTriggerRefs.current[i] = createRef<HTMLButtonElement>();
    }
    if (!toolTipTargetRefs.current[i]) {
      toolTipTargetRefs.current[i] = createRef<HTMLLIElement>();
    }
  });

  if (!fees || fees.length === 0) {
    return null;
  }

  return (
    <ul>
      {fees.map((fee, index) => {
        return (
          <li className="relative" ref={toolTipTargetRefs.current[index]} key={`fee-${index}`}>
            <DataGrid.Row>
              <DataGrid.Cell
                title={
                  <div className="gap-xs flex items-center">
                    <span>{_t(`${baseTranslationKey}.fee.${fee.type}.label`)}</span>
                    <span className="flex">
                      <button
                        className="w-max cursor-pointer outline-none focus:outline-none"
                        ref={toolTipTriggerRefs.current[index]}
                        aria-expanded={openTooltipIndex === index}
                        aria-controls="popover"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltipIndex(index);
                        }}>
                        <Icon variant="info-circle" size="xs" color="fg_secondary" />
                      </button>
                    </span>
                  </div>
                }
                type="description"
              />
              <DataGrid.Cell
                title={fee.amount}
                type="description"
                align="right"
                ariaLabel={_t(`${baseTranslationKey}.fee.onlinePayment.label`)}
              />
            </DataGrid.Row>
            {openTooltipIndex === index && (
              <Tooltip
                placement="bottom"
                onClose={() => setOpenTooltipIndex(null)}
                triggerRef={toolTipTriggerRefs.current[index]}
                targetRef={toolTipTargetRefs.current[index]}
                offsetLeft={10}
                offsetTop={30}
                onClickOutside={(e) => {
                  if (e) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                  setOpenTooltipIndex(null);
                }}>
                <div className="p-lg space-y-md">
                  <p className="text-s text-fg_primary_inverse">
                    {_t(`${baseTranslationKey}.fee.${fee.type}.info`, { fee: fee.amount })}
                  </p>
                </div>
              </Tooltip>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default PaymentDetailsFeesList;
